import { Component } from 'preact';
import { toast } from 'sonner';
import RuneSpinner from './RuneSpinner';
import { fetchLibraryDecks, toggleDeckLike, recordDeckView } from '../utils/arena/deckLibraryApi';
import { timeAgo } from '../utils/arena/timeFormat';
import PlayerTitle from './PlayerTitle';
import DeckTile, { DeckTileText, TXT } from './DeckTile';
import { isPhoneLayout } from '../utils/platform';
import { TabBar } from './ui/TabBar';
import { t, k } from '../localization/t';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  TAB_ACTIVE_TEXT, TAB_INACTIVE,
} from '../lib/medievalTheme';

const TABS = [
  { key: 'all', label: k('All') },
  { key: 'following', label: k('Friends') },
  { key: 'liked', label: k('Liked') },
];

const SORTS = [
  { key: 'latest', label: k('Latest') },
  { key: 'popular', label: k('Popular') },
  { key: 'name', label: k('Name') },
  { key: 'trending', label: k('Trending') },
];

function getAvatarOptions(sorceryCards) {
  if (!Array.isArray(sorceryCards)) return [];
  return sorceryCards
    .filter((c) => c.type === 'Avatar')
    .map((c) => ({ value: c.unique_id, label: c.name }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export default class DeckLibrary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      decks: [],
      loading: true,
      tab: 'all',
      sort: 'latest',
      search: '',
      avatarFilter: null,
      showSortMenu: false,
      showAvatarMenu: false,
    };
  }

  componentDidMount() {
    this.loadDecks();
  }

  loadDecks = async () => {
    // Use props when filter bar is hidden (parent owns the state)
    const tab = this.props.filterTab || this.state.tab;
    const sort = this.props.filterSort || this.state.sort;
    const search = (this.props.filterSearch ?? this.state.search).trim() || undefined;
    const avatar = this.props.filterAvatar || this.state.avatarFilter || undefined;
    const type = this.props.filterType || undefined;
    this.setState({ loading: true });
    try {
      const decks = await fetchLibraryDecks({ tab, sort, search, avatar, type });
      this.setState({ decks: Array.isArray(decks) ? decks : [], loading: false });
    } catch (err) {
      console.error('[DeckLibrary] Failed to load:', err);
      this.setState({ decks: [], loading: false });
    }
  };

  setTab = (tab) => {
    this.setState({ tab }, this.loadDecks);
  };

  setSort = (sort) => {
    this.setState({ sort }, this.loadDecks);
  };

  handleSearch = (e) => {
    this.setState({ search: e.target.value });
    clearTimeout(this._searchTimer);
    this._searchTimer = setTimeout(this.loadDecks, 300);
  };

  handleLike = async (deckId) => {
    try {
      const result = await toggleDeckLike(deckId);
      this.setState((s) => ({
        decks: s.decks.map((d) =>
          d.id === deckId ? { ...d, liked: result.liked, likes: result.likes } : d
        ),
      }));
    } catch (err) {
      toast.error(t('Failed to like deck'));
    }
  };

  handleOpenDeck = (deck) => {
    recordDeckView(deck.id).catch(() => {});
    this.setState((s) => ({
      decks: s.decks.map((d) =>
        d.id === deck.id ? { ...d, views: (d.views || 0) + 1 } : d
      ),
    }));
    if (this.props.onOpenDeck) {
      this.props.onOpenDeck({ ...deck, views: (deck.views || 0) + 1 });
    }
  };

  renderDeckTile = (deck, index) => {
    const { sorceryCards } = this.props;

    const footerLeft = (
      <>
        {DeckTileText.title(deck.name)}
        <div className="flex items-center gap-1.5 mt-0.5">
          {deck.updatedAt ? <span style={{ color: TEXT_MUTED, fontSize: TXT(0.58, 0.75) }}>{timeAgo(deck.updatedAt)}</span> : null}
          <span className="tabular-nums" style={{ color: TEXT_MUTED, fontSize: TXT(0.58, 0.75) }}>· {deck.cardCount === 1 ? t('1 card') : t('{n} cards', { n: deck.cardCount })}</span>
        </div>
      </>
    );

    const footerRight = (
      <>
        <span className="truncate block" style={{ color: ACCENT_GOLD, fontSize: TXT(0.62, 0.75) }}>
          @{deck.playerName || t('Unknown')}
        </span>
        {deck.playerId ? (
          <PlayerTitle
            player={{ id: deck.playerId, displayedCrownAvatarId: deck.playerDisplayedCrownAvatarId, isFounder: !!deck.playerIsFounder, isQaTester: !!deck.playerIsQaTester }}
            sorceryCards={sorceryCards}
            prefix=""
            style={{ fontSize: TXT(0.55, 0.625), display: 'block', lineHeight: 1.1 }}
            className="truncate"
          />
        ) : null}
        <div className="flex items-center gap-2 mt-0.5">
          <span className="flex items-center gap-0.5 tabular-nums" style={{ color: TEXT_MUTED, fontSize: TXT(0.58, 0.75) }}>
            <svg style={{ width: TXT(0.58, 0.75), height: TXT(0.58, 0.75) }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
            {deck.views || 0}
          </span>
          <button
            type="button"
            // Negative margin pads the hit area without moving the glyph.
            className="flex items-center gap-0.5 tabular-nums transition-[color,transform] duration-150 active:scale-[0.96] px-1.5 py-1 -mx-1.5 -my-1"
            style={{ color: deck.liked ? '#e85050' : TEXT_MUTED, fontSize: TXT(0.58, 0.75) }}
            onClick={(e) => { e.stopPropagation(); this.handleLike(deck.id); }}
          >
            {deck.liked ? '♥' : '♡'} {deck.likes || 0}
          </button>
        </div>
      </>
    );

    return (
      <DeckTile
        key={deck.id}
        deck={deck}
        sorceryCards={sorceryCards}
        onClick={() => this.handleOpenDeck(deck)}
        footerLeftSlot={footerLeft}
        footerRightSlot={footerRight}
        enterIndex={index}
      />
    );
  };

  // Called by DeckGallery to render the library's filter controls
  // inline in the gallery's single toolbar row.
  renderInlineControls() {
    const { tab, sort, search } = this.state;
    return (
      <>
        {/* Unified search bar */}
        <div
          className="flex items-center flex-1 max-w-[42cqw] rounded-lg overflow-visible"
          style={{ background: '#0e0b08', border: `1px solid ${GOLD} 0.18)` }}
        >
          <div className="px-2.5 shrink-0" style={{ color: TEXT_MUTED }}>
            <svg style={{ width: '0.8125cqw', height: '0.8125cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </div>
          <div className="w-px h-5 shrink-0" style={{ background: `${GOLD} 0.12)` }} />
          <input
            type="text"
            placeholder={t('Search decks...')}
            value={search}
            onInput={this.handleSearch}
            className="flex-1 min-w-0 px-2.5 py-2 text-xs outline-none"
            style={{ background: 'transparent', color: TEXT_BODY, border: 'none' }}
          />
          <div className="relative shrink-0" style={{ borderLeft: `1px solid ${GOLD} 0.12)` }}>
            <button
              type="button"
              className="flex items-center gap-2 px-3 py-2 text-xs transition-all"
              style={{ color: this.state.avatarFilter ? TEXT_PRIMARY : TEXT_MUTED }}
              onClick={() => this.setState((s) => ({ showAvatarMenu: !s.showAvatarMenu, showSortMenu: false }))}
            >
              <svg style={{ width: '0.875cqw', height: '0.875cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 17l3-3h4l3 3" /><path d="M9 14V6" /><path d="M12 17l3-3h4l3 3" /><path d="M19 14V6" />
              </svg>
              {this.state.avatarFilter
                ? (getAvatarOptions(this.props.sorceryCards).find((a) => a.value === this.state.avatarFilter)?.label || t('Avatar'))
                : t('Any Avatar')}
            </button>
            {this.state.showAvatarMenu && (
              <div className="absolute right-0 top-full mt-1 z-50 rounded-xl overflow-hidden max-h-60 overflow-y-auto" style={{ background: '#0e0b08', border: `1px solid ${GOLD} 0.2)`, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', minWidth: '11.25cqw' }}>
                <button type="button" className="w-full px-4 py-2 text-xs text-left transition-all" style={{ color: !this.state.avatarFilter ? ACCENT_GOLD : TEXT_BODY }} onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }} onClick={() => { this.setState({ avatarFilter: null, showAvatarMenu: false }, this.loadDecks); }}>{t('Any Avatar')}</button>
                {getAvatarOptions(this.props.sorceryCards).map((a) => (
                  <button key={a.value} type="button" className="w-full px-4 py-2 text-xs text-left transition-all" style={{ color: this.state.avatarFilter === a.value ? ACCENT_GOLD : TEXT_BODY }} onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }} onClick={() => { this.setState({ avatarFilter: a.value, showAvatarMenu: false }, this.loadDecks); }}>{a.label}</button>
                ))}
              </div>
            )}
          </div>
          <div className="relative shrink-0" style={{ borderLeft: `1px solid ${GOLD} 0.12)` }}>
            <button
              type="button"
              className="flex items-center gap-2 px-3 py-2 text-xs transition-all"
              style={{ color: TEXT_PRIMARY }}
              onClick={() => this.setState((s) => ({ showSortMenu: !s.showSortMenu, showAvatarMenu: false }))}
            >
              <svg style={{ width: '0.875cqw', height: '0.875cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 3v18M3 7l4-4 4 4M17 21V3M21 17l-4 4-4-4" />
              </svg>
              {t(SORTS.find((s) => s.key === sort)?.label || 'Latest')}
            </button>
            {this.state.showSortMenu && (
              <div className="absolute right-0 top-full mt-1 z-50 rounded-xl overflow-hidden" style={{ background: '#0e0b08', border: `1px solid ${GOLD} 0.2)`, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', minWidth: '10cqw' }}>
                {SORTS.map((s) => (
                  <button key={s.key} type="button" className="w-full px-4 py-2.5 text-xs text-left transition-all" style={{ color: sort === s.key ? ACCENT_GOLD : TEXT_BODY }} onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }} onClick={() => { this.setState({ showSortMenu: false }); this.setSort(s.key); }}>{t(s.label)}</button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sub-tabs */}
        <TabBar className="flex items-center shrink-0 rounded-lg" style={{ background: '#0e0b08', border: `1px solid ${GOLD} 0.18)`, padding: '0.125cqw' }} activeIndex={TABS.findIndex((item) => item.key === tab)}>
          {TABS.map((item) => (
            <button
              key={item.key}
              type="button"
              data-tab-item
              className="px-3 py-1.5 text-[0.625cqw] font-semibold"
              style={tab === item.key ? TAB_ACTIVE_TEXT : TAB_INACTIVE}
              onClick={() => this.setTab(item.key)}
            >
              {t(item.label)}
            </button>
          ))}
        </TabBar>
      </>
    );
  }

  render() {
    const { decks, loading, tab, sort, search } = this.state;

    return (
      <div className="flex flex-col h-full">
        {/* Filter row. Hidden when parent renders controls inline */}
        {this.props.hideFilterBar ? null : <div className="shrink-0 px-6 pt-4 pb-3 flex items-center gap-3">
          {/* Unified search bar. Search + avatar + sort in one component */}
          <div
            className="flex items-center flex-1 max-w-[42cqw] rounded-lg overflow-visible"
            style={{ background: '#0e0b08', border: `1px solid ${GOLD} 0.18)` }}
          >
            {/* Search icon */}
            <div className="px-2.5 shrink-0" style={{ color: TEXT_MUTED }}>
              <svg style={{ width: '0.8125cqw', height: '0.8125cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <div className="w-px h-5 shrink-0" style={{ background: `${GOLD} 0.12)` }} />
            {/* Search input */}
            <input
              type="text"
              placeholder={t('Search decks...')}
              value={search}
              onInput={this.handleSearch}
              className="flex-1 min-w-0 px-2.5 py-2 text-xs outline-none"
              style={{ background: 'transparent', color: TEXT_BODY, border: 'none' }}
            />

            {/* Avatar dropdown */}
            <div className="relative shrink-0" style={{ borderLeft: `1px solid ${GOLD} 0.12)` }}>
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-2 text-xs transition-all"
                style={{ color: this.state.avatarFilter ? TEXT_PRIMARY : TEXT_MUTED }}
                onClick={() => this.setState((s) => ({ showAvatarMenu: !s.showAvatarMenu, showSortMenu: false }))}
              >
                <svg style={{ width: '0.875cqw', height: '0.875cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 17l3-3h4l3 3" /><path d="M9 14V6" /><path d="M12 17l3-3h4l3 3" /><path d="M19 14V6" />
                </svg>
                {this.state.avatarFilter
                  ? (getAvatarOptions(this.props.sorceryCards).find((a) => a.value === this.state.avatarFilter)?.label || t('Avatar'))
                  : t('Any Avatar')}
              </button>
              {this.state.showAvatarMenu && (
                <div
                  className="absolute right-0 top-full mt-1 z-50 rounded-xl overflow-hidden max-h-60 overflow-y-auto"
                  style={{ background: 'rgba(18,14,10,0.98)', border: `1px solid ${GOLD} 0.2)`, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', minWidth: '11.25cqw' }}
                >
                  <button
                    type="button"
                    className="w-full px-4 py-2 text-xs text-left transition-all"
                    style={{ color: !this.state.avatarFilter ? ACCENT_GOLD : TEXT_BODY }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    onClick={() => { this.setState({ avatarFilter: null, showAvatarMenu: false }, this.loadDecks); }}
                  >
                    {t('Any Avatar')}
                  </button>
                  {getAvatarOptions(this.props.sorceryCards).map((a) => (
                    <button
                      key={a.value}
                      type="button"
                      className="w-full px-4 py-2 text-xs text-left transition-all"
                      style={{ color: this.state.avatarFilter === a.value ? ACCENT_GOLD : TEXT_BODY }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      onClick={() => { this.setState({ avatarFilter: a.value, showAvatarMenu: false }, this.loadDecks); }}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Sort dropdown */}
            <div className="relative shrink-0" style={{ borderLeft: `1px solid ${GOLD} 0.12)` }}>
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-2 text-xs transition-all"
                style={{ color: TEXT_PRIMARY }}
                onClick={() => this.setState((s) => ({ showSortMenu: !s.showSortMenu, showAvatarMenu: false }))}
              >
                <svg style={{ width: '0.875cqw', height: '0.875cqw' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 3v18M3 7l4-4 4 4M17 21V3M21 17l-4 4-4-4" />
                </svg>
                {t(SORTS.find((s) => s.key === sort)?.label || 'Latest')}
              </button>
              {this.state.showSortMenu && (
                <div
                  className="absolute right-0 top-full mt-1 z-50 rounded-xl overflow-hidden"
                  style={{ background: 'rgba(18,14,10,0.98)', border: `1px solid ${GOLD} 0.2)`, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', minWidth: '10cqw' }}
                >
                  {SORTS.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      className="w-full px-4 py-2.5 text-xs text-left transition-all"
                      style={{ color: sort === s.key ? ACCENT_GOLD : TEXT_BODY }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      onClick={() => { this.setState({ showSortMenu: false }); this.setSort(s.key); }}
                    >
                      {t(s.label)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sub-tabs */}
          <TabBar className="flex items-center shrink-0 rounded-lg" style={{ background: '#0e0b08', border: `1px solid ${GOLD} 0.18)`, padding: '0.125cqw' }} activeIndex={TABS.findIndex((item) => item.key === tab)}>
            {TABS.map((item) => (
              <button
                key={item.key}
                type="button"
                data-tab-item
                className="px-3 py-1.5 text-[0.625cqw] font-semibold"
                style={tab === item.key ? TAB_ACTIVE_TEXT : TAB_INACTIVE}
                onClick={() => this.setTab(item.key)}
              >
                {t(item.label)}
              </button>
            ))}
          </TabBar>
        </div>}

        {/* Deck list. Padding lets every tile's hover effects (scale,
            fan spread, lift) play out without being clipped by the
            scroll container's edges. `overflow-y: auto` implicitly
            clips overflow-x, so we can't rely on visible-overflow; the
            tile's outward growth has to fit inside this padding. */}
        <div
          className="flex-1 overflow-y-auto min-h-0"
          onScroll={this.props.onBodyScroll}
          style={isPhoneLayout() ? {
            // Matches the gallery's overlay chrome height (see
            // DeckGallery: constant padding keeps the scroller stable
            // while the chrome slides).
            paddingTop: 'calc(7.25rem + env(safe-area-inset-top, 0px))',
            paddingLeft: 'max(0.75rem, env(safe-area-inset-left, 0px))',
            paddingRight: 'max(0.75rem, env(safe-area-inset-right, 0px))',
            paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))',
          } : { padding: '0.75cqw 1.75cqw 1cqw' }}
        >
          {loading ? (
            <div className="flex items-center justify-center py-12"><RuneSpinner size={28} /></div>
          ) : decks.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-xs" style={{ color: TEXT_MUTED }}>
                {tab === 'liked' ? t('No liked decks yet') :
                 tab === 'following' ? t('No decks from friends yet') :
                 t('No shared decks yet')}
              </p>
            </div>
          ) : (
            <div className={isPhoneLayout() ? 'grid gap-3' : 'grid gap-5'} style={{ gridTemplateColumns: isPhoneLayout() ? 'repeat(3, minmax(0, 1fr))' : 'repeat(auto-fill, minmax(22cqw, 1fr))' }}>
              {decks.map((deck, i) => this.renderDeckTile(deck, i))}
            </div>
          )}
        </div>

      </div>
    );
  }
}
